import { ReactElement } from 'react';

import {
  FormControlLabel,
  FormControlLabelProps,
  styled,
  SwitchProps as MuiSwitchProps,
} from '@mui/material';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import { AccessSwitch } from '../AccessSwitch';

const StyledFormControlLabel = styled(FormControlLabel)({
  '& .MuiTypography-root, &.MuiFormControlLabel-label': {
    color: '#202227',
    fontSize: 13,
    fontWeight: 700,
  },
  '&.MuiFormControlLabel-labelPlacementStart': {
    marginLeft: 0,
    '& .MuiFormControlLabel-label': {
      marginRight: 8,
    },
  },
});

export type SwitchWithLabelProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<MuiSwitchProps, 'checked'> & {
  ControllerProps: Omit<ControllerProps<TFieldValues, TName>, 'render'>;
  FormControlLabelProps: Omit<FormControlLabelProps, 'control'>;
};

export const SwitchWithLabel = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ControllerProps,
  FormControlLabelProps,
  ...props
}: SwitchWithLabelProps<TFieldValues, TName>): ReactElement => {
  return (
    <Controller
      {...ControllerProps}
      render={({ field: { onChange, ref, ...field } }) => (
        <StyledFormControlLabel
          control={
            <AccessSwitch
              {...props}
              {...field}
              inputRef={ref}
              onChange={(event, checked) => {
                onChange(event);
                props.onChange && props.onChange(event, checked);
              }}
              checked={field.value as boolean}
            />
          }
          labelPlacement="start"
          {...FormControlLabelProps}
          sx={{
            '&.MuiFormControlLabel-labelPlacementEnd': {
              marginRight: 0,
              '& .MuiFormControlLabel-label': {
                marginLeft: 1,
              },
            },
            ...FormControlLabelProps.sx,
          }}
        />
      )}
    />
  );
};
